import { assert, FilterGroup, logger } from '@fe/core';
import { FlightLayerFacade } from '@fe/flight-layer';
import { FeMap } from '@fe/maplibre-extensions';
import { MapMouseEvent } from 'maplibre-gl';
import { SlotLayer } from '../layers/custom/slot/slot-layer';
import { barbSizeToMultiplier } from '../layers/geojson/styles/common';
import { WeatherLayerBase } from '../layers/weather-layer-base';
import { WeatherSettings, WeatherSettingsChangeSource } from '../settings/settings';
import { MessageType } from '../worker/actor/actor-messages';
import { Dispatcher } from '../worker/dispatcher';
import { WeatherStoreProductInfo } from '../worker/store/weather-store';
import { generateImages } from './images';
import { layerFactory } from './layer-factory';
import { WeatherPickingInfoLayer } from './types';

const WEATHER_SLOT_NAME = 'fe-weather-end';

export class WeatherLayerManager {
    public flightLayerFacade: FlightLayerFacade | null = null;

    private readonly layers = new Map<number, WeatherLayerBase>();
    // FIXME: remove order from here, because it's already handled by the weather
    private order: number[] = [];
    private map: FeMap | null = null;
    private settings: WeatherSettings | null = null;

    constructor(
        private readonly workspaceId: number,
        private readonly dispatcher: Dispatcher
    ) {}

    async addToMap(map: FeMap, beforeId?: string) {
        await generateImages(map);
        this.map = map;
        this.map.addLayer(new SlotLayer(WEATHER_SLOT_NAME), beforeId);
    }

    removeFromMap() {
        assert(this.map);
        this.layers.forEach((x) => x.remove());
        this.layers.clear();
        this.map = null;
        this.order = [];
    }

    addProduct(product: WeatherStoreProductInfo): void {
        if (this.layers.has(product.id)) {
            return;
        }

        assert(this.map);
        const layer = layerFactory(
            this.dispatcher,
            this.workspaceId,
            product,
            this.map,
            WEATHER_SLOT_NAME,
            this.settings!,
            this.flightLayerFacade
        );
        if (!layer) {
            logger.warn('No layer implementation for product %d', product.id);
            return;
        }
        this.order.push(product.id);
        this.layers.set(product.id, layer);
        layer.add();
    }

    loadProduct(product: WeatherStoreProductInfo): void {
        const layer = this.layers.get(product.id);
        if (!layer) {
            return;
        }

        layer.productUpdated(product);
    }

    showOneFrame(id: number, frame: number) {
        const layer = this.layers.get(id);
        if (!layer) {
            return;
        }

        layer.setVisibleFrame(frame);
    }

    setVisibility(id: number, visibility: boolean) {
        const layer = this.layers.get(id);
        if (!layer) {
            return;
        }

        layer.setVisibility(visibility);
    }

    removeProduct(id: number) {
        const layer = this.layers.get(id);
        if (!layer) {
            return;
        }
        this.layers.delete(id);
        this.order = this.order.filter((x) => x !== id);
        layer.remove();
    }

    removeProducts(ids: number[]) {
        for (const id of ids) {
            const layer = this.layers.get(id);
            if (layer) {
                this.layers.delete(id);
                layer.remove();
            }
        }
        this.order = this.order.filter((x) => !ids.includes(x));
    }

    moveProduct(id: number, before?: number | undefined): void {
        const prodIdx = this.order.indexOf(id);
        if (prodIdx < 0) {
            return;
        }

        let newIdx = before ? this.order.indexOf(before) : this.order.length;
        if (newIdx < 0) {
            newIdx = this.order.length;
        }

        this.order.splice(prodIdx, 1);
        this.order.splice(newIdx, 0, id);

        const layer = this.layers.get(id);
        const beforeProductFirstLayerId = this.layers.get(before ?? -1)?.getBottomSlot();

        layer?.move(beforeProductFirstLayerId);
    }

    async updateSettings(settings: WeatherSettings, changeSource: WeatherSettingsChangeSource) {
        this.settings = settings;
        if (changeSource === WeatherSettingsChangeSource.unknown) {
            logger.warn('Rogue change source: unknown');
        } else {
            logger.debug("Settings changed. Reason: '%s'", WeatherSettingsChangeSource[changeSource]);
        }
        await this.dispatcher.dispatch({
            type: MessageType.updateSettings,
            data: {
                settings,
                workspaceId: this.workspaceId
            }
        });

        this.map?.setGlobalStateProperty(
            'polygon-transparency',
            settings.misc.enablePolygonTransparency ? settings.misc.polygonTransparency / 100 : 1.0
        );
        this.map?.setGlobalStateProperty(
            'trf-transparency',
            settings.misc.enablePolygonTransparency && settings.airspace.tfrApplyTransparency
                ? settings.misc.polygonTransparency / 100
                : 1.0
        );

        this.map?.setGlobalStateProperty('wind-barbs-size', barbSizeToMultiplier(settings.winds.barbSize));

        this.map?.setGlobalStateProperty('wind-barbs-color-type', settings.winds.barbColorType);
        this.map?.setGlobalStateProperty('wind-barbs-fixed-color', settings.winds.barbColor);

        for (const layer of this.layers.values()) {
            layer.updateSettings(settings, changeSource);
        }
    }

    // NOTE: filter groups and my airports are updated here from layer manager which in multi workspace environment means that they are updated multiple times in weather worker
    // This has no negative consequences but since filter groups and my airports are workspace independent they should be update outside of workspace scoped class like this one
    // We should update those only once outside and then dispatch reload layer commands to each workspace layer stack
    async updateAirportFilterGroups(filterGroup: FilterGroup[]) {
        await this.dispatcher.dispatch({ type: MessageType.updateAirportFilterGroup, data: filterGroup });
        if (!this.settings?.airport.filterGroup) {
            return;
        }

        // Update layers
        for (const layer of this.layers.values()) {
            layer.updateSettings(this.settings, WeatherSettingsChangeSource.airportFilterGroup);
        }
    }

    async updateSuaFilterGroup(filterGroup: FilterGroup[]) {
        await this.dispatcher.dispatch({ type: MessageType.updateSuaFilterGroup, data: filterGroup });
        if (!this.settings?.dsua.filterGroup) {
            return;
        }

        // Update layers
        for (const layer of this.layers.values()) {
            layer.updateSettings(this.settings, WeatherSettingsChangeSource.dsua);
        }
    }

    async updateMyAirports(airports: string[]) {
        await this.dispatcher.dispatch({ type: MessageType.updateMyAirports, data: airports });

        if (!this.settings?.airport.myFleetAirports) {
            return;
        }

        // Update layers
        for (const layer of this.layers.values()) {
            layer.updateSettings(this.settings, WeatherSettingsChangeSource.airportMyAirports);
        }
    }

    async pickObjects(event: MapMouseEvent): Promise<WeatherPickingInfoLayer[]> {
        const layers = [] as WeatherPickingInfoLayer[];
        for (let i = this.order.length - 1; i >= 0; i--) {
            const layerId = this.order[i];
            const layer = this.layers.get(layerId);
            if (!layer?.shouldDisplay()) {
                continue;
            }

            const objects = await layer.pickObjects(event);
            if (!objects) {
                continue;
            }

            if (Array.isArray(objects)) {
                layers.push(...objects);
            } else {
                layers.push(objects);
            }
        }
        return layers;
    }
}
